import React, { useEffect, useState, useCallback } from 'react';

import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';

import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Typography from '@mui/material/Typography';

import SparklineGroup from './charts/sparklinegroup';
import { NestMetrics } from '../d3/utils';
// import { useWhatChangedEffect } from '../libraries/withwhyrender';

import Moment from 'moment';

const useStyles = makeStyles(theme => ({
    root: {
    },
    panelHeading: {
        ...theme.typography.button,
        paddingLeft: theme.spacing(1),
    },
    panelDetails: {
        paddingTop: 0,
        marginTop: -15,
    }
}));

const EXPAND_HEIGHT = 400;
const CHART_HEIGHT = 50;

const HealthPanel = (props) => {
    const classes = useStyles();
    // const navigate = useNavigate();

    const [metrics, setMetrics] = useState([]);
    const [height, setHeight] = useState(0);
    const [start, setStart] = useState(Moment().subtract(1, 'days').utc().toISOString());
    const [end, setEnd] = useState(Moment().utc().toISOString());
    const [settings, setSettings] = useState({
        margin: 40,
        labelWidth: 150,
        vgrid: true,
        barWidth: 2,
        expandHeight: EXPAND_HEIGHT,
        chartHeight: CHART_HEIGHT,
    });
    const [expanded, setExpanded] = useState();

    const handleOnClickSparkline = useCallback((d, metric, key) => {
        setExpanded(current => {
            if (current && current.key === key && current.metric === metric) return undefined;
            return { key, metric };
        });
    }, []);

    // update settings
    useEffect(() => {
        // echo friendly updates - don't change object pointer
        setSettings(current => {
            current.chartHeight = props.info.name === 'issues' ? 35 : CHART_HEIGHT;
            current.scale = props.scale || 'logarithmic';
            return current;
        });
    }, [props.scale, props.info]);

    // update settings and nest 'nested' data (e.g. disks, apps)
    useEffect(() => {
        const history = (props.data.history || [])
            .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

        if (history.length > 0) {
            setStart(history[0].timestamp);
            setEnd(history[history.length - 1].timestamp);
        }
        let _metrics = [];
        if (props.info.nested) {
            _metrics = NestMetrics(props.info, history, ['redis-rdb-bgsave']);
        } else {
            // otherwise there's only one timelines group
            _metrics = [{
                index: 0,
                timeline: history,
                key: props.info.name,
                metrics: props.info.metrics
            }];
        }
        const _height = props.info.metrics.length * (props.info.name === 'issues' ? 35 : CHART_HEIGHT);
        setHeight(_height);
        setMetrics(_metrics);
    }, [props.scale, props.info, props.data, expanded]);

    return (
        <Accordion
            TransitionProps={{ unmountOnExit: true }}
            expanded={props.expanded}
            onChange={props.onExpand}>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={props.info.name + '-content'}>
                {props.info.icon}
                <Typography className={classes.panelHeading}>{props.info.label}</Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.panelDetails}>
                <SparklineGroup
                    metrics={metrics}
                    height={height}
                    start={start}
                    end={end}
                    settings={settings}
                    onClick={handleOnClickSparkline}
                    expand={expanded}
                    key={props.info.name}>
                </SparklineGroup>
            </AccordionDetails>
        </Accordion>
    );
};

HealthPanel.propTypes = {
    data: PropTypes.object.isRequired,
    info: PropTypes.object.isRequired,
    scale: PropTypes.string,
    className: PropTypes.string,
    expanded: PropTypes.bool,
    onExpand: PropTypes.func,
    uuid: PropTypes.string,
    type: PropTypes.oneOf(['sensor', 'cmc', 'mc2']),
};

export default HealthPanel;
